import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Download, ExternalLink, Search } from 'lucide-react';
import { buildPageList } from '../../utils/gmpBoard';
import { consensusBucket } from '../../utils/ipoReviews';
import { VERDICT_OPTIONS, consensusLabel } from '../../utils/smeReviews';

const COLUMN_COUNT = 6;
const MAX_BROKERS_SHOWN = 3;

const VERDICT_STYLES = {
  apply: 'bg-emerald-600 border-emerald-600 text-white',
  may: 'bg-sky-50 border-sky-200 text-sky-700',
  avoid: 'bg-rose-50 border-rose-200 text-rose-600',
  awaited: 'bg-slate-50 border-slate-200 text-slate-500',
};

const CALL_TONES = { positive: 'text-emerald-600', cautious: 'text-amber-600', neutral: 'text-slate-500', negative: 'text-rose-600' };

const FIELD_CLASS =
  'px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 placeholder-slate-400 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]';

const BrokerCell = ({ row }) => {
  if (!row.brokers.length) return <span className="text-[11px] italic text-slate-400">Awaiting broker research notes</span>;
  const shown = row.brokers.slice(0, MAX_BROKERS_SHOWN);
  const hidden = row.brokers.slice(MAX_BROKERS_SHOWN);
  return (
    <ul className="space-y-1 max-w-[280px]">
      {shown.map((broker) => (
        <li key={broker.name} className="flex items-start justify-between gap-3 text-[11px] leading-snug">
          <span className="text-slate-700">{broker.name}</span>
          <span className={`font-semibold text-right whitespace-nowrap ${CALL_TONES[broker.tone]}`}>{broker.call}</span>
        </li>
      ))}
      {hidden.length > 0 && (
        <li className="text-[11px] font-semibold text-slate-400" title={hidden.map((broker) => `${broker.name}: ${broker.call}`).join('\n')}>
          +{hidden.length} more {hidden.length === 1 ? 'broker' : 'brokers'}
        </li>
      )}
    </ul>
  );
};

const NoteCell = ({ row }) => {
  if (!row.noteUrl) return <span className="text-xs text-slate-300">Pending</span>;
  if (consensusBucket(row) === 'apply') {
    return (
      <a
        href={row.noteUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={`${row.noteBroker} research note`}
        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#006666] hover:bg-[#004d4d] text-white text-[11px] font-bold whitespace-nowrap"
      >
        Read Report
        <ArrowRight className="w-3.5 h-3.5" />
      </a>
    );
  }
  return (
    <a
      href={row.noteUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={`${row.noteBroker} research note`}
      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#008080] whitespace-nowrap"
    >
      View Note
      <ExternalLink className="w-3 h-3" />
    </a>
  );
};

export const SmeReviewsTable = forwardRef(
  (
    {
      rows,
      smeCount,
      year,
      page,
      pageSize,
      onPageChange,
      query,
      onQueryChange,
      broker,
      onBrokerChange,
      brokers,
      verdict,
      onVerdictChange,
      onExport,
      isLoading,
      emptyMessage,
    },
    ref
  ) => {
    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const pageRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const firstIndex = rows.length ? (currentPage - 1) * pageSize + 1 : 0;
    const lastIndex = Math.min(currentPage * pageSize, rows.length);

    return (
      <div ref={ref} className="scroll-mt-20 space-y-3">
        {/* Board tabs: All and Mainboard live on the combined reviews page */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <nav aria-label="Review boards" className="inline-flex gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200/80">
            <Link to="/ipo-reviews" className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:text-slate-900 whitespace-nowrap">
              All IPOs
            </Link>
            <Link
              to="/ipo-reviews?tab=mainboard"
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:text-slate-900 whitespace-nowrap"
            >
              Mainboard IPOs
            </Link>
            <span aria-current="page" className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#008080] text-white shadow-sm whitespace-nowrap">
              SME IPOs (Active)
            </span>
          </nav>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-500">
              Total Records: <strong className="text-slate-800">{smeCount}</strong>
            </span>
            <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-500">
              Year:
              <select value={year} disabled className="bg-transparent font-bold text-slate-800 focus:outline-none disabled:opacity-100">
                <option value={year}>Year {year}</option>
              </select>
            </label>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_220px_180px_auto] gap-2">
          <label className="relative block">
            <span className="sr-only">Search company</span>
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search company or sector..."
              className={`${FIELD_CLASS} w-full pl-8`}
            />
          </label>
          <label className="block">
            <span className="sr-only">Filter by brokerage</span>
            <select value={broker} onChange={(e) => onBrokerChange(e.target.value)} className={`${FIELD_CLASS} w-full font-semibold`}>
              <option value="all">All Brokerages &amp; Analysts</option>
              {brokers.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="sr-only">Filter by verdict</span>
            <select value={verdict} onChange={(e) => onVerdictChange(e.target.value)} className={`${FIELD_CLASS} w-full font-semibold`}>
              <option value="all">All Verdicts</option>
              {VERDICT_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={onExport}
            disabled={!rows.length}
            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>

        <section aria-label="SME IPO reviews" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
          <div className="overflow-x-auto scrollbar-slim">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  <th scope="col" className="sticky left-0 z-10 bg-slate-50 py-3 pl-4 sm:pl-5 pr-3 min-w-[200px] sm:min-w-[240px]">
                    Issuer Company Name
                  </th>
                  <th scope="col" className="py-3 px-2 whitespace-nowrap">Type</th>
                  <th scope="col" className="py-3 px-3 min-w-[220px]">Evaluating Research House / Broker</th>
                  <th scope="col" className="py-3 px-2 text-center whitespace-nowrap">Consensus Verdict</th>
                  <th scope="col" className="py-3 px-2 text-center whitespace-nowrap">CM Score</th>
                  <th scope="col" className="py-3 pl-2 pr-4 sm:pr-5 text-right whitespace-nowrap">Research Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
                {isLoading &&
                  Array.from({ length: 6 }).map((_, idx) => (
                    <tr key={`sme-review-skel-${idx}`} className="animate-pulse">
                      <td className="sticky left-0 z-10 bg-white py-3.5 pl-4 sm:pl-5 pr-3">
                        <div className="w-44 h-4 bg-slate-200 rounded" />
                        <div className="mt-1.5 w-28 h-3 bg-slate-100 rounded" />
                      </td>
                      {Array.from({ length: COLUMN_COUNT - 1 }).map((__, col) => (
                        <td key={col} className="py-3.5 px-3">
                          <div className="w-16 h-4 bg-slate-100 rounded" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && pageRows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="py-12 text-center text-sm text-slate-500">
                      {emptyMessage}
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  pageRows.map((row) => {
                    const bucket = consensusBucket(row);
                    const featured = bucket === 'apply';
                    return (
                      <tr key={row.id} className={`group align-middle transition-colors ${featured ? 'bg-emerald-50/50' : 'hover:bg-slate-50/70'}`}>
                        <td className={`sticky left-0 z-10 py-3.5 pl-4 sm:pl-5 pr-3 ${featured ? 'bg-emerald-50' : 'bg-white group-hover:bg-slate-50'}`}>
                          <Link to={`/ipo-gmp/${row.gmpSlug}`} className="font-bold text-slate-900 hover:text-[#008080]">
                            {row.company}
                          </Link>
                          <p className="mt-0.5 text-[11px] text-slate-400">{[row.platform, row.sector].filter(Boolean).join(' • ')}</p>
                        </td>
                        <td className="py-3.5 px-2">
                          <span className="inline-flex px-2 py-0.5 rounded-md border border-slate-200 bg-slate-50 text-[10px] font-extrabold text-slate-600">
                            SME
                          </span>
                        </td>
                        <td className="py-3.5 px-3">
                          <BrokerCell row={row} />
                        </td>
                        <td className="py-3.5 px-2 text-center">
                          <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full border text-[11px] font-bold whitespace-nowrap ${VERDICT_STYLES[bucket]}`}>
                            {consensusLabel(row)}
                          </span>
                          {row.consensusText && (
                            <p className="mt-1 text-[10px] text-slate-400 max-w-[170px] mx-auto leading-snug">{row.consensusText}</p>
                          )}
                        </td>
                        <td className="py-3.5 px-2 text-center">
                          {row.cmScore !== null ? (
                            <span className="text-sm font-extrabold text-slate-800 tabular-nums">{row.cmScore}/100</span>
                          ) : (
                            <span className="text-slate-300" title="No Capital Market score published yet">
                              --
                            </span>
                          )}
                        </td>
                        <td className="py-3.5 pl-2 pr-4 sm:pr-5 text-right">
                          <NoteCell row={row} />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-200/80 bg-slate-50/70">
            <p className="text-xs text-slate-500">
              {isLoading
                ? 'Loading SME IPO reviews…'
                : `Showing ${firstIndex} to ${lastIndex} of ${rows.length} SME IPO reviews for Year ${year}`}
            </p>
            {totalPages > 1 && (
              <nav className="flex flex-wrap items-center justify-center gap-1" aria-label="SME reviews pagination">
                <button
                  type="button"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                  className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {buildPageList(currentPage, totalPages).map((p, i) =>
                  p === '…' ? (
                    <span key={`gap-${i}`} className="w-7 text-center text-xs text-slate-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => onPageChange(p)}
                      aria-current={p === currentPage ? 'page' : undefined}
                      className={`w-8 h-8 rounded-lg text-xs font-bold border ${
                        p === currentPage ? 'bg-[#008080] border-[#008080] text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                  className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </nav>
            )}
          </div>
        </section>
      </div>
    );
  }
);

SmeReviewsTable.displayName = 'SmeReviewsTable';

export default SmeReviewsTable;
